import React, { useLayoutEffect, useRef, useState } from 'react';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { Form } from 'react-final-form';

import Box from '@material-ui/core/Box';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';

import { Dialog } from 'components/common';
import { Checkbox } from 'components/form';
import { formatCentsPrice } from 'services/helpers/formatPrice';

import {
  AchAllocation,
  AchTransactionRecord,
  AchBankVerificationResponse,
  BankVerificationState,
  BankVerificationFormValues,
} from './achVerificationTypes';
import { apiErrorMessage, formatDateTime, shortIntent } from './achVerificationHelpers';

const useStyles = makeStyles((theme) => ({
  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: theme.spacing(1.5),
  },

  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    fontSize: 13,
  },

  label: {
    color: theme.palette.text.secondary,
  },

  previous: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    fontSize: 13,
  },

  result: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    backgroundColor: '#edf7ed',
    border: '1px solid #2e7d32',
    borderRadius: 4,
    fontSize: 13,
    wordBreak: 'break-all',
  },

  strong: {
    fontWeight: 600,
  },

  resultTitle: {
    fontSize: 13,
    fontWeight: 600,
  },

  hint: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
}));

interface Props {
  open: boolean;
  onClose: () => void;
  // rollo user id (uuid)
  userId: string;
  // transaction group record of the pending ACH deposit
  record: AchTransactionRecord;
  // when opened from another dialog: called on close after a request was sent, instead of refreshing the
  // datagrid here (the refresh re-renders the row and would unmount the parent dialog)
  onRequested?: () => void;
}

/**
 * RLPR-20420 asks the user to relink the bank account of a pending ACH deposit through Stripe Financial Connections
 * with the transactions permission, so the admin can later check whether the debit posted at the bank.
 */
const RequestBankVerificationDialog = ({ open, onClose, userId, record, onRequested }: Props) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [error, setError] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<AchBankVerificationResponse | null>(null);
  const mountedRef = useRef(false);
  const pendingRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const requestedRef = useRef(false);

  const paymentIntent: string | undefined = record?.details?.paymentIntentId;
  const allocation: AchAllocation | undefined = record?.details?.achAllocation;
  const previous: BankVerificationState | undefined = record?.details?.bankVerification;

  useLayoutEffect(() => {
    mountedRef.current = true;
    pendingRef.current = false;
    setError(undefined);
    setResult(null);
    return () => {
      mountedRef.current = false;
      requestSequenceRef.current += 1;
      if (requestedRef.current) {
        requestedRef.current = false;
        refresh();
      }
    };
  }, [userId, paymentIntent, refresh]);

  const request = async (values: BankVerificationFormValues) => {
    if (pendingRef.current || !mountedRef.current) {
      return;
    }
    pendingRef.current = true;
    const requestId = ++requestSequenceRef.current;
    const isCurrentRequest = () => mountedRef.current && requestSequenceRef.current === requestId;
    try {
      setError(undefined);
      const { data } = await dataProvider.post('requestBankVerification', {
        userId,
        data: { paymentIntent, sendEmail: !!values.sendEmail },
      });
      if (!isCurrentRequest()) {
        refresh();
        return;
      }
      requestedRef.current = true;
      setResult(data);
      notify(
        data.emailSent
          ? 'Bank verification requested, the user was emailed the link'
          : 'Bank verification requested, hand the link to the user',
        'info'
      );
      // the deposit row now carries the request in details.bankVerification; the datagrid is refreshed on
      // close, a refresh here would re-render the row and unmount this dialog before the link can be copied
    } catch (e) {
      if (isCurrentRequest()) {
        setError(apiErrorMessage(e, 'Could not request the bank verification'));
      }
    } finally {
      if (isCurrentRequest()) {
        pendingRef.current = false;
      }
    }
  };

  const handleClose = () => {
    if (pendingRef.current) {
      return;
    }
    if (requestedRef.current) {
      requestedRef.current = false;
      if (onRequested) {
        onRequested();
      } else {
        refresh();
      }
    }
    onClose();
  };

  return (
    <Form<BankVerificationFormValues>
      onSubmit={request}
      initialValues={{ sendEmail: true }}
      render={({ submitting, handleSubmit }) => (
        <Dialog
          open={open}
          title="Request bank verification"
          onCancel={handleClose}
          cancelText={result ? 'Close' : 'Cancel'}
          onConfirm={result ? undefined : handleSubmit}
          confirmText="Send request"
          confirmDisabled={!paymentIntent}
          submitting={submitting}
          dismissDisabled={submitting}
          error={error}
          note="The user signs in to the Rollo portal and relinks the bank account through Stripe. Rollo never sees the bank login. Bank accounts linked before this feature only share balances, so the relink is needed once per bank account.">
          <Box className={classes.rows}>
            <Box className={classes.row}>
              <span className={classes.label}>Deposit</span>
              <span>{shortIntent(paymentIntent)}</span>
            </Box>
            <Box className={classes.row}>
              <span className={classes.label}>Deposit amount</span>
              <span>{formatCentsPrice(allocation?.baseAmountCents ?? 0)}</span>
            </Box>
            <Box className={classes.row}>
              <span className={classes.label}>Still pending</span>
              <span className={classes.strong}>
                {formatCentsPrice(allocation?.pendingAmountCents ?? 0)}
              </span>
            </Box>
          </Box>

          {previous?.requestedAt && !result && (
            <Box className={classes.previous}>
              Already requested on {formatDateTime(previous.requestedAt)} by{' '}
              {previous.requestedBy || 'an admin'}
              {previous.emailSent ? ', the user was emailed the link.' : ', without email.'} Sending
              it again is harmless.
            </Box>
          )}

          {result ? (
            <Box className={classes.result}>
              <Typography className={classes.resultTitle}>
                {result.emailSent ? 'Link emailed to the user' : 'Link ready to hand over'}
              </Typography>
              <Link href={result.portalUrl} target="_blank" rel="noopener noreferrer">
                {result.portalUrl}
              </Link>
              <Typography className={classes.hint}>
                Once the user finished, use &quot;Check bank transactions&quot; on this deposit.
              </Typography>
            </Box>
          ) : (
            <>
              <Checkbox
                name="sendEmail"
                label="Email the link to the user"
                inputProps={{ disabled: submitting }}
              />
              <Typography className={classes.hint}>
                The link is shown here after the request either way, so support can also send it
                through another channel.
              </Typography>
            </>
          )}
        </Dialog>
      )}
    />
  );
};

export default RequestBankVerificationDialog;
